import React, { Component } from 'react'
import axios from 'axios'
import moment from 'moment'
import { Row, Col, Tag, Skeleton, Modal, message } from 'antd'
import { connect } from 'react-redux'
import { ButtonItem } from '../../../../Components/Items'
import DatePickerItem from '../../../../Components/DatePickerItem'
import { ExclamationCircleOutlined } from '@ant-design/icons'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0)

const fmtFecha = (f) => {
  if (!f) return '—'
  const s = (f || '').split('T')[0]
  const parts = s.split('-')
  if (parts.length !== 3) return s
  return `${parts[2]}/${parts[1]}/${parts[0]}`
}

const TIPO_LABEL = {
  vacaciones_y_prima: 'Vac + Prima',
  solo_prima:         'Solo Prima',
  solo_vacaciones:    'Solo Vacaciones',
}

// Días de vacaciones (hábiles) y días de descanso del rango. El descanso se separa a propósito:
// NO se paga como vacaciones, lo paga el concepto Séptimo Día, y sólo los hábiles se descuentan
// del kárdex (Art. 76 LFT habla de días laborables). El motor aplica el mismo criterio al armar
// las incidencias del periodo (bloque [vac-pre] de Controllers/Prenomina/calculo.controllers.js).
//
// Ojo: aquí el día de descanso es el domingo (getDay() === 0) mientras que el motor usa
// Periodo.posisionSeptimoDia, que es una POSICIÓN configurable dentro del periodo. Coinciden en
// la configuración normal —semanal/catorcenal que arranca en lunes—; si una empresa mueve el
// descanso o el arranque del periodo, este desglose es indicativo y manda el motor.
// Duplicadas en Controllers/Nomina/vacacionesMasivas.js, que es de donde las toman el batch
// masivo y la importación de incidencias por Excel.
function calcularDiasHabiles(inicio, fin) {
  if (!inicio || !fin) return 0
  let count = 0
  const current = new Date(inicio + 'T12:00:00')
  const end = new Date(fin + 'T12:00:00')
  while (current <= end) {
    if (current.getDay() !== 0) count++
    current.setDate(current.getDate() + 1)
  }
  return count
}

function calcularSeptimos(inicio, fin) {
  if (!inicio || !fin) return 0
  let count = 0
  const current = new Date(inicio + 'T12:00:00')
  const end = new Date(fin + 'T12:00:00')
  while (current <= end) {
    if (current.getDay() === 0) count++
    current.setDate(current.getDate() + 1)
  }
  return count
}

// ─── Estilos tabla ────────────────────────────────────────────────────────────

const TH = {
  border: '1px solid #d0d0d0', background: '#f5f5f5',
  padding: '5px 8px', fontSize: 11, fontWeight: 600,
  color: '#555', textAlign: 'center', whiteSpace: 'nowrap',
}
const TD = {
  border: '1px solid #e8e8e8', padding: '4px 8px',
  fontSize: 12, verticalAlign: 'middle',
}
const TD_NUM = { ...TD, textAlign: 'right', fontFamily: 'monospace' }

// ─── Estilos formulario ───────────────────────────────────────────────────────

const labelStyle = { display: 'block', fontSize: 11, color: '#888', marginBottom: 3 }
const inputStyle = {
  width: '100%', padding: '4px 8px', border: '1px solid #d0d0d0',
  borderRadius: 4, fontSize: 12, boxSizing: 'border-box',
}
const selectStyle = { ...inputStyle, background: 'white', cursor: 'pointer', height: 28 }
const btnInlineStyle = {
  fontSize: 11, padding: '2px 8px', borderRadius: 4,
  border: '1px solid', cursor: 'pointer', background: 'white',
}

// ─────────────────────────────────────────────────────────────────────────────

class VacacionesEmpleado extends Component {

  state = {
    cargando: false,
    error: null,
    kardex: null,
    movimientos: [],
    historial: [],
    factorPrima: 0.25,
    sueldoDiario: 0,
    // form
    formVisible: false,
    editandoId: null,
    tipo: 'vacaciones_y_prima',
    fechaInicio: '',
    fechaFin: '',
    fechaPago: '',
    guardando: false,
  }

  componentDidMount() {
    this.cargarDatos()
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.empleadoId !== this.props.empleadoId ||
      prevProps.periodoId  !== this.props.periodoId  ||
      prevProps.empresaId  !== this.props.empresaId
    ) {
      this.resetForm()
      this.cargarDatos()
    }
  }

  resetForm() {
    this.setState({
      formVisible: false, editandoId: null,
      tipo: 'vacaciones_y_prima', fechaInicio: '', fechaFin: '', fechaPago: '',
    })
  }

  // ── Fetch ──────────────────────────────────────────────────────────────────

  async cargarDatos() {
    const { empleadoId, periodoId, empresaId, headersToken } = this.props
    if (!empleadoId || !empresaId) return
    this.setState({ cargando: true, error: null })
    try {
      if (periodoId) {
        const res = await axios.post(
          'nomina/movimientosVacaciones',
          { empleadoId, periodoId, empresaId },
          { headers: headersToken }
        )
        if (res.data.success === 2) {
          // El endpoint manda { diasDerechoAnio, diasTomados, diasDisponibles }, la misma forma
          // que kardexVacaciones, para que renderCards no tenga que distinguir los dos modos.
          this.setState({
            kardex:       res.data.kardex       || null,
            movimientos:  res.data.movimientos  || [],
            factorPrima:  res.data.factorPrima  ?? 0.25,
            sueldoDiario: res.data.sueldoDiario ?? 0,
          })
        } else {
          this.setState({ error: res.data.mensaje || res.data.err || 'Error al cargar vacaciones del período' })
        }
      } else {
        const res = await axios.post(
          'nomina/kardexVacaciones',
          { empleadoId, empresaId },
          { headers: headersToken }
        )
        if (res.data.success === 2) {
          this.setState({
            kardex: {
              diasDerechoAnio:  res.data.diasDerechoAnio,
              diasTomados:      res.data.diasTomados,
              diasDisponibles:  res.data.diasDisponibles,
            },
            historial:    res.data.historial    || [],
            factorPrima:  res.data.factorPrima  ?? 0.25,
            sueldoDiario: res.data.sueldoDiario ?? 0,
          })
        } else {
          this.setState({ error: res.data.mensaje || res.data.err || 'Error al cargar kárdex de vacaciones' })
        }
      }
    } catch {
      this.setState({ error: 'Error de conexión al cargar vacaciones' })
    }
    this.setState({ cargando: false })
  }

  // ── Cálculos del formulario ────────────────────────────────────────────────

  getDiasForm() {
    const { tipo, fechaInicio, fechaFin } = this.state
    const deshabilitarFechas = tipo === 'solo_prima'
    const diasVacaciones = deshabilitarFechas ? 0 : calcularDiasHabiles(fechaInicio, fechaFin)
    const diasSeptimos   = deshabilitarFechas ? 0 : calcularSeptimos(fechaInicio, fechaFin)
    // solo_prima = caso 2 → diasPrima = 0; los otros casos → diasPrima = diasVacaciones
    const diasPrima = tipo === 'solo_prima' ? 0 : diasVacaciones
    return { diasVacaciones, diasSeptimos, diasPrima }
  }

  // ── Acciones ───────────────────────────────────────────────────────────────

  confirmarCancelar(movimientoId) {
    const { periodoId } = this.props
    Modal.confirm({
      title: '¿Cancelar este movimiento de vacaciones?',
      // El aviso del recálculo sólo aplica en el tab del sobre-recibo (modo con periodoId):
      // cancelar no recalcula la prenómina, así que hasta que no se recalcule, el recibo sigue
      // mostrando y pagando las vacaciones canceladas.
      content: periodoId
        ? 'Los días regresarán al saldo disponible. Después tienes que RECALCULAR la nómina: '
          + 'mientras no lo hagas, el recibo va a seguir mostrando y pagando estas vacaciones.'
        : 'Los días regresarán al saldo disponible.',
      icon: <ExclamationCircleOutlined />,
      okText: 'Sí, cancelar',
      cancelText: 'No',
      onOk: () => this.cancelarMovimiento(movimientoId),
    })
  }

  async cancelarMovimiento(movimientoId) {
    const { periodoId, empresaId, headersToken } = this.props
    try {
      const res = await axios.post(
        'nomina/cancelarVacacion',
        { movimientoId, periodoId, empresaId },
        { headers: headersToken }
      )
      if (res.data.success === 2) {
        message.success('Movimiento cancelado')
        this.cargarDatos()
        this.props.onCambio?.()
        // El backend avisa que la línea quedó desfasada. Va como warning y con duración larga
        // porque es un paso que el usuario TIENE que dar: si no recalcula, ve el movimiento
        // cancelado aquí pero el recibo sigue pagando las vacaciones, y parece que no sirvió.
        if (res.data.requiereRecalculo && periodoId) {
          message.warning('Recalcula la nómina para que el recibo deje de reflejar estas vacaciones.', 8)
        }
      } else {
        // El backend responde en `mensaje` (no en `err`): aquí es donde llega el motivo cuando
        // la línea ya está timbrada o es de una nómina histórica.
        message.error(res.data.mensaje || res.data.err || 'Error al cancelar el movimiento')
      }
    } catch {
      message.error('Error de conexión')
    }
  }

  abrirEdicion(mov) {
    this.setState({
      formVisible:  true,
      editandoId:   mov._id,
      tipo:         mov.tipo,
      fechaInicio:  (mov.fechaInicio || '').split('T')[0],
      fechaFin:     (mov.fechaFin    || '').split('T')[0],
      fechaPago:    (mov.fechaPago   || '').split('T')[0],
    })
  }

  async guardarVacacion() {
    const { empleadoId, periodoId, empresaId, headersToken } = this.props
    const { tipo, fechaInicio, fechaFin, fechaPago, editandoId, sueldoDiario, factorPrima } = this.state
    const { diasVacaciones, diasSeptimos, diasPrima } = this.getDiasForm()

    if (!fechaPago) { message.error('Ingresa la fecha de pago'); return }
    if (tipo !== 'solo_prima' && (!fechaInicio || !fechaFin)) {
      message.error('Ingresa fecha inicial y final'); return
    }

    const importeVacaciones = diasVacaciones * sueldoDiario
    const importePrima      = diasPrima * sueldoDiario * factorPrima

    const body = {
      empleadoId, periodoId: periodoId || null, empresaId,
      tipo, fechaInicio, fechaFin, fechaPago,
      diasVacaciones, diasPrima, diasSeptimos, factorPrima,
      importeVacaciones, importePrima,
    }
    if (editandoId) body.movimientoId = editandoId

    this.setState({ guardando: true })
    try {
      const endpoint = editandoId ? 'nomina/editarVacacion' : 'nomina/crearVacacion'
      const res = await axios.post(endpoint, body, { headers: headersToken })
      if (res.data.success === 2) {
        message.success(editandoId ? 'Vacación actualizada' : 'Vacación registrada')
        this.resetForm()
        this.cargarDatos()
        this.props.onCambio?.()
      } else {
        // `mensaje`, no `err`: por aquí llega el aviso de que el movimiento traslapa con otro ya
        // registrado. Leyéndolo como `err` se mostraba un "Error al guardar" genérico y el motivo
        // real —el traslape, que es justo lo que duplica días de vacaciones— quedaba invisible.
        message.error(res.data.mensaje || res.data.err || 'Error al guardar')
      }
    } catch {
      message.error('Error de conexión')
    }
    this.setState({ guardando: false })
  }

  // ── Renders ────────────────────────────────────────────────────────────────

  // Las mismas tres tarjetas en los dos modos (con y sin periodoId). Antes el modo del
  // sobre-recibo pintaba "Días disponibles" y "Días restantes" como si fueran cosas distintas:
  // el segundo venía del backend como `diasDisponibles - diasTomados`, o sea con los tomados
  // restados DOS veces, y contradecía a la primera. El saldo es uno solo: derecho - tomados.
  renderCards() {
    const { kardex } = this.state

    const disponibles = kardex?.diasDisponibles ?? 0
    const cards = [
      { label: 'Días con derecho', val: kardex?.diasDerechoAnio ?? 0, color: '#00BFD6', bg: '#e6feff' },
      { label: 'Días tomados',     val: kardex?.diasTomados     ?? 0, color: '#595959', bg: '#f5f5f5' },
      { label: 'Días disponibles', val: disponibles,
        color: disponibles > 0 ? '#3B6D11' : '#A32D2D',
        bg:    disponibles > 0 ? '#f6ffed' : '#fff1f0' },
    ]

    return (
      <Row gutter={12} style={{ marginBottom: 16 }}>
        {cards.map(({ label, val, color, bg }) => (
          <Col span={8} key={label}>
            <div style={{ background: bg, borderRadius: 6, padding: '10px 14px' }}>
              <div style={{ fontSize: 11, color }}>{label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color }}>{val}</div>
              <div style={{ fontSize: 11, color: '#aaa' }}>días</div>
            </div>
          </Col>
        ))}
      </Row>
    )
  }

  renderTablaMovimientos() {
    const { movimientos } = this.state
    const { timbrado } = this.props

    return (
      <div style={{ overflowX: 'auto', marginBottom: 12 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'auto' }}>
          <thead>
            <tr>
              <th style={TH}>Tipo</th>
              <th style={TH}>Fecha Inicio</th>
              <th style={TH}>Fecha Fin</th>
              <th style={TH}>Días Vac</th>
              <th style={TH}>Días Prima</th>
              <th style={TH}>Séptimos</th>
              <th style={TH}>Importe Vac</th>
              <th style={TH}>Importe Prima</th>
              <th style={TH}>Estatus</th>
              {!timbrado && <th style={TH}>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {movimientos.length === 0 && (
              <tr>
                <td colSpan={timbrado ? 9 : 10} style={{ ...TD, textAlign: 'center', color: '#aaa', padding: 24 }}>
                  Sin vacaciones registradas en este período
                </td>
              </tr>
            )}
            {movimientos.map((mov) => {
              const cancelado    = mov.estatus === 0
              const esEditable   = mov.estatus === 1 && !timbrado
              const rowBg        = cancelado ? '#f5f5f5' : mov.esPeriodoActual ? '#e6f7ff' : 'white'
              const textStyle    = cancelado ? { textDecoration: 'line-through', color: '#aaa' } : {}

              return (
                <tr key={mov._id} style={{ background: rowBg }}>
                  <td style={{ ...TD, ...textStyle }}>{TIPO_LABEL[mov.tipo] || mov.tipo}</td>
                  <td style={{ ...TD, ...textStyle }}>{fmtFecha(mov.fechaInicio)}</td>
                  <td style={{ ...TD, ...textStyle }}>{fmtFecha(mov.fechaFin)}</td>
                  <td style={{ ...TD_NUM, ...textStyle }}>{mov.diasVacaciones}</td>
                  <td style={{ ...TD_NUM, ...textStyle }}>{mov.diasPrima}</td>
                  <td style={{ ...TD_NUM, ...textStyle }}>{mov.diasSeptimos}</td>
                  <td style={{ ...TD_NUM, ...textStyle }}>{fmtMXN(mov.importeVacaciones)}</td>
                  <td style={{ ...TD_NUM, ...textStyle }}>{fmtMXN(mov.importePrima)}</td>
                  <td style={{ ...TD, textAlign: 'center' }}>
                    {cancelado
                      ? <Tag color="default" style={{ color: '#aaa' }}>Cancelado</Tag>
                      : <Tag color="green">Activo</Tag>
                    }
                  </td>
                  {!timbrado && (
                    <td style={{ ...TD, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {esEditable && (
                        <>
                          <button type="button"
                            onClick={() => this.abrirEdicion(mov)}
                            style={{ ...btnInlineStyle, color: '#1677ff', borderColor: '#1677ff' }}
                          >
                            Editar
                          </button>
                          {' '}
                          <button type="button"
                            onClick={() => this.confirmarCancelar(mov._id)}
                            style={{ ...btnInlineStyle, color: '#ff4d4f', borderColor: '#ff4d4f' }}
                          >
                            Cancelar
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  renderHistorial() {
    const { historial } = this.state
    const { timbrado, readOnly } = this.props
    const puedeEditar = !readOnly && !timbrado
    const sorted = [...historial].sort((a, b) => new Date(b.fecha) - new Date(a.fecha))

    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={TH}>Fecha registro</th>
              <th style={TH}>Tipo</th>
              <th style={TH}>Fecha inicio</th>
              <th style={TH}>Fecha fin</th>
              <th style={TH}>Días</th>
              <th style={TH}>Importe</th>
              <th style={TH}>Período</th>
              <th style={TH}>Estatus</th>
              {puedeEditar && <th style={TH}>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr>
                <td colSpan={puedeEditar ? 9 : 8} style={{ ...TD, textAlign: 'center', color: '#aaa', padding: 24 }}>
                  Sin historial de vacaciones
                </td>
              </tr>
            )}
            {sorted.map((h) => {
              const cancelado = h.estatus === 0
              const esEditable = h.estatus === 1 && puedeEditar
              const textStyle = cancelado ? { textDecoration: 'line-through', color: '#aaa' } : {}
              return (
                <tr key={h._id} style={{ background: cancelado ? '#f5f5f5' : 'white' }}>
                  <td style={{ ...TD, ...textStyle }}>{fmtFecha(h.fecha)}</td>
                  <td style={{ ...TD, ...textStyle }}>{TIPO_LABEL[h.tipo] || h.tipo}</td>
                  <td style={{ ...TD, ...textStyle }}>{fmtFecha(h.fechaInicio)}</td>
                  <td style={{ ...TD, ...textStyle }}>{fmtFecha(h.fechaFin)}</td>
                  <td style={{ ...TD_NUM, ...textStyle }}>{h.dias}</td>
                  <td style={{ ...TD_NUM, ...textStyle }}>{fmtMXN(h.importe)}</td>
                  <td style={{ ...TD, ...textStyle }}>{h.periodo || '—'}</td>
                  <td style={{ ...TD, textAlign: 'center' }}>
                    {cancelado
                      ? <Tag color="default" style={{ color: '#aaa' }}>Cancelado</Tag>
                      : <Tag color="green">Activo</Tag>
                    }
                  </td>
                  {puedeEditar && (
                    <td style={{ ...TD, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {esEditable && (
                        <>
                          <button type="button"
                            onClick={() => this.abrirEdicion(h)}
                            style={{ ...btnInlineStyle, color: '#1677ff', borderColor: '#1677ff' }}
                          >
                            Editar
                          </button>
                          {' '}
                          <button type="button"
                            onClick={() => this.confirmarCancelar(h._id)}
                            style={{ ...btnInlineStyle, color: '#ff4d4f', borderColor: '#ff4d4f' }}
                          >
                            Cancelar
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  renderFormulario() {
    const { tipo, fechaInicio, fechaFin, fechaPago, guardando, editandoId, factorPrima, kardex, sueldoDiario } = this.state
    const { periodoId } = this.props
    const { diasVacaciones, diasSeptimos, diasPrima } = this.getDiasForm()

    const importeVacaciones = diasVacaciones * sueldoDiario
    const importePrima      = diasPrima * sueldoDiario * factorPrima

    const diasTomados      = kardex?.diasTomados    ?? 0
    // `diasDisponibles` YA trae los tomados descontados (derecho - tomados). Restarlos otra vez
    // aquí es lo que hacía que el saldo saliera corto y que el aviso rojo se disparara con días
    // de sobra: un empleado con 12 de derecho que tomó 5 y pide 6 mostraba -4 en vez de 1.
    const diasDisponibles  = kardex?.diasDisponibles ?? 0
    const periodoRegreso   = diasDisponibles - diasVacaciones
    const regresoNegativo  = periodoRegreso < 0

    const deshabilitarFechas = tipo === 'solo_prima'

    return (
      <div style={{ border: '1px solid #d0d0d0', borderRadius: 6, padding: 16, marginBottom: 16, background: '#fafcff' }}>
        <div style={{ fontWeight: 600, marginBottom: 12, color: '#555', fontSize: 13 }}>
          {editandoId ? 'Editar vacación' : 'Nueva vacación'}
        </div>

        {/* Tipo */}
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Tipo de captura</label>
          <select
            value={tipo}
            onChange={e => this.setState({ tipo: e.target.value })}
            style={selectStyle}
          >
            <option value="vacaciones_y_prima">Vacaciones y prima vacacional</option>
            {periodoId && <option value="solo_prima">Solo prima vacacional</option>}
            <option value="solo_vacaciones">Solo vacaciones</option>
          </select>
        </div>

        {/* Fechas */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Fecha inicial</label>
            <DatePickerItem
              value={fechaInicio ? moment(fechaInicio, 'YYYY-MM-DD') : null}
              disabled={deshabilitarFechas}
              onChange={(d) => this.setState({ fechaInicio: d ? d.format('YYYY-MM-DD') : '' })}
              style={{ ...inputStyle, width: '100%', opacity: deshabilitarFechas ? 0.5 : 1 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Fecha final</label>
            <DatePickerItem
              value={fechaFin ? moment(fechaFin, 'YYYY-MM-DD') : null}
              disabled={deshabilitarFechas}
              onChange={(d) => this.setState({ fechaFin: d ? d.format('YYYY-MM-DD') : '' })}
              style={{ ...inputStyle, width: '100%', opacity: deshabilitarFechas ? 0.5 : 1 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Fecha de pago</label>
            <DatePickerItem
              value={fechaPago ? moment(fechaPago, 'YYYY-MM-DD') : null}
              onChange={(d) => this.setState({ fechaPago: d ? d.format('YYYY-MM-DD') : '' })}
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
        </div>

        {/* Campos calculados */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Días de vacaciones</label>
            <input
              type="number" readOnly value={diasVacaciones}
              style={{ ...inputStyle, background: '#f5f5f5', color: '#1677ff', fontWeight: 600 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Séptimos</label>
            <input
              type="number" readOnly value={diasSeptimos}
              style={{ ...inputStyle, background: '#f5f5f5', color: '#1677ff', fontWeight: 600 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Días prima</label>
            <input
              type="number" readOnly value={diasPrima}
              style={{ ...inputStyle, background: '#f5f5f5', color: '#1677ff', fontWeight: 600 }}
            />
          </div>
        </div>

        {/* Desglose de lo que va a pagar la nómina. Los días de descanso del rango NO se pagan
            como vacaciones: los paga el concepto Séptimo Día, íntegro. Sin esto no había forma
            de saber por qué un rango de 7 días naturales paga "6 + 1" y no 7 de vacaciones. */}
        {!deshabilitarFechas && (diasVacaciones > 0 || diasSeptimos > 0) && (
          <div style={{ marginBottom: 12, fontSize: 11, color: '#555', background: '#f0f9ff', border: '1px solid #bae0ff', borderRadius: 4, padding: '6px 10px' }}>
            La nómina pagará <b>Vacaciones {diasVacaciones} día{diasVacaciones === 1 ? '' : 's'}</b>
            {' + '}<b>Séptimo día {diasSeptimos}</b> = <b>{diasVacaciones + diasSeptimos} días</b> del periodo.
            {' '}Al kárdex se le descuentan los <b>{diasVacaciones}</b> días de vacaciones.
          </div>
        )}

        {/* Importes y factor */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Importe vacaciones</label>
            <input
              type="text" readOnly value={fmtMXN(importeVacaciones)}
              style={{ ...inputStyle, background: '#f5f5f5', color: '#3B6D11', fontWeight: 600 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Importe prima</label>
            <input
              type="text" readOnly value={fmtMXN(importePrima)}
              style={{ ...inputStyle, background: '#f5f5f5', color: '#3B6D11', fontWeight: 600 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Factor prima</label>
            <input
              type="text" readOnly value={`${(factorPrima * 100).toFixed(0)}%`}
              style={{ ...inputStyle, background: '#f5f5f5' }}
            />
          </div>
        </div>

        {/* Tablita resumen Kárdex */}
        <div style={{ marginBottom: 12 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
            <thead>
              <tr>
                <th style={{ ...TH, textAlign: 'left', background: '#f0f0f0' }}></th>
                <th style={{ ...TH, background: '#f0f0f0' }}>Días Vac</th>
                <th style={{ ...TH, background: '#f0f0f0' }}>Días de descanso</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ ...TD, fontWeight: 500, fontSize: 11 }}>Días de esta captura</td>
                <td style={{ ...TD_NUM, fontSize: 11 }}>{diasVacaciones}</td>
                <td style={{ ...TD_NUM, fontSize: 11 }}>{diasSeptimos}</td>
              </tr>
              <tr>
                <td style={{ ...TD, fontWeight: 500, fontSize: 11 }}>Días ya tomados en el año</td>
                <td style={{ ...TD_NUM, fontSize: 11 }}>{diasTomados}</td>
                <td style={{ ...TD_NUM, fontSize: 11 }}>—</td>
              </tr>
              <tr style={{ background: regresoNegativo ? '#fff1f0' : 'white' }}>
                <td style={{ ...TD, fontWeight: 500, fontSize: 11, color: regresoNegativo ? '#A32D2D' : 'inherit' }}>
                  Le quedarían
                </td>
                <td style={{ ...TD_NUM, fontSize: 11, color: regresoNegativo ? '#A32D2D' : 'inherit' }}>
                  {periodoRegreso}
                </td>
                <td style={{ ...TD_NUM, fontSize: 11 }}>—</td>
              </tr>
            </tbody>
          </table>
          {regresoNegativo && (
            <div style={{ color: '#A32D2D', fontSize: 11, marginTop: 4 }}>
              ⚠ El empleado supera los días disponibles
            </div>
          )}
        </div>

        {/* Botones */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <ButtonItem size="small" onClick={() => this.resetForm()}>
            Cancelar captura
          </ButtonItem>
          <ButtonItem type="primary" size="small" loading={guardando} onClick={() => this.guardarVacacion()}>
            Guardar
          </ButtonItem>
        </div>
      </div>
    )
  }

  render() {
    const { cargando, error, formVisible } = this.state
    const { timbrado, readOnly, periodoId } = this.props

    if (cargando) {
      return <Skeleton active paragraph={{ rows: 4 }} />
    }

    if (error) {
      return (
        <div style={{ color: '#A32D2D', padding: '10px 14px', background: '#fff1f0', borderRadius: 4, fontSize: 12 }}>
          {error}
        </div>
      )
    }

    const puedeEditar = !readOnly && !timbrado

    // ── Modo Tab (periodoId provisto) ────────────────────────────────────────
    if (periodoId) {
      return (
        <div>
          {timbrado && (
            <div style={{ marginBottom: 12, padding: '7px 14px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, fontSize: 12, color: '#7c5a00' }}>
              Nómina timbrada — visualización de solo lectura.
            </div>
          )}
          {this.renderTablaMovimientos()}
          {puedeEditar && !formVisible && (
            <div style={{ marginBottom: 12 }}>
              <ButtonItem
                size="small"
                type="dashed"
                onClick={() => this.setState({ formVisible: true, editandoId: null, tipo: 'vacaciones_y_prima', fechaInicio: '', fechaFin: '', fechaPago: '' })}
              >
                + Agregar vacaciones
              </ButtonItem>
            </div>
          )}
          {formVisible && this.renderFormulario()}
        </div>
      )
    }

    // ── Modo Perfil empleado (sin periodoId) ─────────────────────────────────
    return (
      <div>
        {timbrado && (
          <div style={{ marginBottom: 12, padding: '7px 14px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, fontSize: 12, color: '#7c5a00' }}>
            Nómina timbrada — visualización de solo lectura.
          </div>
        )}
        {this.renderCards()}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#555' }}>Historial de vacaciones</div>
          {puedeEditar && !formVisible && (
            <ButtonItem size="small" type="primary" onClick={() => this.setState({ formVisible: true, editandoId: null })}>
              Registrar vacaciones
            </ButtonItem>
          )}
        </div>
        {formVisible && this.renderFormulario()}
        {this.renderHistorial()}
      </div>
    )
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId:    state.empresa.id,
})

export default connect(mapStateToProps)(VacacionesEmpleado)
